export interface ActionPermissionConfig {
  requiresConfirmation: boolean
  defaultButtonLabel?: string
  description?: string
  requiresDesignTab?: boolean
  requiresStudioLoaded?: boolean
  requiresSelectedSystem?: boolean
  hideActionAccepted?: boolean
  requiresPanels?: boolean
  requiresInverterAdded?: boolean
  requiresBattery?: boolean
  requiresSinglePanelType?: boolean
  requiresSingleInverterType?: boolean
}

export const ACTION_PERMISSIONS: Record<string, ActionPermissionConfig> = {
  autoDesignEphemeral: {
    requiresConfirmation: true,
    defaultButtonLabel: 'Run Auto Design',
    description: 'This will run the auto-design process to generate solar system designs.',
    requiresStudioLoaded: true,
    hideActionAccepted: true,
  },

  saveSystemDesignAsTemplate: {
    requiresConfirmation: true,
    defaultButtonLabel: 'Save as Template',
    description: 'This will save the currently selected system design as a reusable template.',
    requiresDesignTab: true,
    requiresSelectedSystem: true,
  },

  navigate: {
    requiresConfirmation: false,
    defaultButtonLabel: 'Navigate',
  },

  loadAutoDesignResult: {
    requiresConfirmation: true,
    defaultButtonLabel: 'Load Results',
    description: 'This will load the auto-design results into your project.',
  },

  loadInverterDesignAssistantResult: {
    requiresConfirmation: false,
    description: 'This will load the selected inverter recommendations into your system.',
  },

  loadBatteryDesignAssistantResult: {
    requiresConfirmation: false,
    description: 'This will load the selected battery recommendations into your system.',
  },

  runAutoString: {
    requiresConfirmation: true,
    defaultButtonLabel: 'Run Auto String',
    description: 'This will automatically string panels to inverters. This will replace current stringing.',
    requiresDesignTab: true,
    requiresStudioLoaded: true,
    requiresSelectedSystem: true,
    requiresPanels: true,
    requiresInverterAdded: true,
    requiresSinglePanelType: true,
    requiresSingleInverterType: true,
  },

  runBatteryDesignAssistant: {
    requiresConfirmation: true,
    defaultButtonLabel: 'Get Battery Recommendations',
    description: 'This will analyze your system and provide battery recommendations based on your objectives.',
    requiresDesignTab: true,
    requiresStudioLoaded: true,
    requiresSelectedSystem: true,
    requiresPanels: true,
  },

  runInverterDesignAssistant: {
    requiresConfirmation: true,
    defaultButtonLabel: 'Get Inverter Recommendations',
    description:
      'This will analyze your system and provide inverter recommendations based on your panel configuration.',
    requiresDesignTab: true,
    requiresStudioLoaded: true,
    requiresSelectedSystem: true,
    requiresPanels: true,
  },

  systemEdit: {
    requiresConfirmation: true,
    defaultButtonLabel: 'Apply System Changes',
    description: 'This will modify the system by adding, removing, or updating components and settings.',
    requiresDesignTab: true,
    requiresStudioLoaded: true,
    requiresSelectedSystem: true,
  },

  undoRedo: {
    requiresConfirmation: true,
    defaultButtonLabel: 'Undo and Redo',
    description:
      'This will undo or redo the last action in the design editor. Use this to revert or reapply changes made to your system design.',
    requiresDesignTab: true,
    requiresStudioLoaded: true,
  },
}

export const requiresConfirmation = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresConfirmation ?? false
}

export const getDefaultButtonLabel = (actionType: string): string | undefined => {
  return ACTION_PERMISSIONS[actionType]?.defaultButtonLabel
}

export const getActionDescription = (actionType: string): string | undefined => {
  return ACTION_PERMISSIONS[actionType]?.description
}

export const getActionTitle = (actionType: string): string | undefined => {
  const titleMap: Record<string, string> = {
    autoDesignEphemeral: 'Run Auto Design',
    saveSystemDesignAsTemplate: 'Save as Template',
    loadAutoDesignResult: 'Load Auto Design Results',
    loadInverterDesignAssistantResult: 'Load Inverter Recommendations',
    loadBatteryDesignAssistantResult: 'Load Battery Recommendations',
    navigate: 'Navigate',
    runAutoString: 'Run Auto String',
    runBatteryDesignAssistant: 'Get Battery Recommendations',
    runInverterDesignAssistant: 'Get Inverter Recommendations',
    systemEdit: 'System Edit',
    undoRedo: 'Undo and Redo',
  }

  return titleMap[actionType]
}

export const requiresDesignTab = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresDesignTab ?? false
}

export const requiresStudioLoaded = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresStudioLoaded ?? false
}

export const requiresSelectedSystem = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresSelectedSystem ?? false
}

export const requiresPanels = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresPanels ?? false
}

export const requiresInverterAdded = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresInverterAdded ?? false
}

export const requiresBattery = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresBattery ?? false
}

export const requiresSinglePanelType = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresSinglePanelType ?? false
}

export const requiresSingleInverterType = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.requiresSingleInverterType ?? false
}

export const hideActionAccepted = (actionType: string): boolean => {
  return ACTION_PERMISSIONS[actionType]?.hideActionAccepted ?? false
}

export const getConfirmableActions = (): Array<{ actionType: string; config: ActionPermissionConfig }> => {
  return Object.entries(ACTION_PERMISSIONS)
    .filter(([_, config]) => config.requiresConfirmation)
    .map(([actionType, config]) => ({ actionType, config }))
}

export const isActionWhitelisted = (actionType: string): boolean => {
  try {
    const stored = localStorage.getItem('ada_action_whitelist')
    if (!stored) return false
    const whitelisted = JSON.parse(stored) as string[]
    return whitelisted.includes(actionType)
  } catch (error) {
    console.error('Error reading action whitelist from localStorage:', error)
    return false
  }
}
